import { Pin } from "lucide-react";
import { TagBadge } from "@/components/ui/TagBadge";
import { VoteButtons } from "@/components/ui/VoteButtons";
import type { IdeaListItem } from "../types";

type Props = {
  idea: IdeaListItem;
  onClick: () => void;
  onTogglePin: () => void;
  onVote: (type: "up" | "down") => void;
  tagLabels?: Record<string, string>;
  categoryLabels?: Record<string, string>;
  statusLabels?: Record<string, string>;
  priorityLabels?: Record<string, string>;
};

function formatRelative(iso: string): string {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  feature: { bg: "hsl(210,85%,95%)", text: "hsl(210,85%,38%)" },
  improvement: { bg: "hsl(180,60%,93%)", text: "hsl(180,70%,28%)" },
  bug: { bg: "hsl(0,70%,94%)", text: "hsl(0,70%,38%)" },
  research: { bg: "hsl(270,70%,96%)", text: "hsl(270,60%,42%)" },
  process: { bg: "hsl(142,55%,94%)", text: "hsl(142,65%,30%)" },
  other: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)" },
};

const PRIORITY_COLORS: Record<string, { bg: string; text: string }> = {
  low: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)" },
  medium: { bg: "hsl(210,85%,95%)", text: "hsl(210,85%,38%)" },
  high: { bg: "hsl(35,85%,94%)", text: "hsl(35,85%,38%)" },
  critical: { bg: "hsl(0,70%,94%)", text: "hsl(0,70%,38%)" },
};

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  captured: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)" },
  exploring: { bg: "hsl(210,85%,95%)", text: "hsl(210,85%,38%)" },
  adopted: { bg: "hsl(142,55%,94%)", text: "hsl(142,65%,30%)" },
  rejected: { bg: "hsl(0,70%,94%)", text: "hsl(0,70%,38%)" },
  on_hold: { bg: "hsl(40,95%,94%)", text: "hsl(35,85%,38%)" },
};

export function IdeaCard({
  idea,
  onClick,
  onTogglePin,
  onVote,
  tagLabels = {},
  categoryLabels = {},
  statusLabels = {},
  priorityLabels = {},
}: Props) {
  const catKey = idea.category ?? "other";
  const catColor = CATEGORY_COLORS[catKey] ?? CATEGORY_COLORS.other;
  const catLabel = idea.category
    ? categoryLabels[idea.category] ?? idea.category
    : "Uncategorized";
  const priorityColor = PRIORITY_COLORS[idea.priority] ?? PRIORITY_COLORS.medium;
  const statusColor = STATUS_COLORS[idea.status] ?? STATUS_COLORS.captured;
  const visibleTags = idea.tags.slice(0, 3);
  const extraTags = idea.tags.length - visibleTags.length;

  return (
    <div
      onClick={onClick}
      className="group flex cursor-pointer flex-col rounded-xl border bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
      style={{ borderColor: "hsl(210,30%,85%)" }}
    >
      <div className="mb-2 flex items-center justify-between">
        <span
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
          style={{ background: catColor.bg, color: catColor.text }}
        >
          {catLabel}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin();
          }}
          title={idea.isPinned ? "Unpin" : "Pin"}
          className="flex h-6 w-6 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
        >
          <Pin
            className="h-[14px] w-[14px]"
            strokeWidth={1.5}
            style={{
              color: idea.isPinned ? "hsl(40,95%,50%)" : "hsl(215,16%,65%)",
              fill: idea.isPinned ? "hsl(40,95%,55%)" : "none",
            }}
          />
        </button>
      </div>

      <div className="mb-2" onClick={(e) => e.stopPropagation()}>
        <VoteButtons
          votesUp={idea.votesUp}
          votesDown={idea.votesDown}
          userVote={idea.currentUserVote}
          onVote={onVote}
          size="sm"
        />
      </div>

      <div
        className="mb-1.5 text-[14px] font-semibold leading-snug text-foreground"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {idea.title}
      </div>

      <div
        className="mb-3 text-[12px] text-muted-foreground"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {idea.contentPreview?.slice(0, 120) || (
          <span className="italic">No content</span>
        )}
      </div>

      {visibleTags.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1">
          {visibleTags.map((t) => (
            <TagBadge key={t} value={t} label={tagLabels[t] ?? t} size="sm" />
          ))}
          {extraTags > 0 && (
            <span className="text-[11px] text-muted-foreground">
              +{extraTags} more
            </span>
          )}
        </div>
      )}

      <div className="mt-auto flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <span
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
            style={{ background: priorityColor.bg, color: priorityColor.text }}
          >
            {priorityLabels[idea.priority] ?? idea.priority}
          </span>
          <span
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
            style={{ background: statusColor.bg, color: statusColor.text }}
          >
            {statusLabels[idea.status] ?? idea.status}
          </span>
        </div>
        <span className="truncate">
          {idea.submittedByName ?? idea.createdBy ?? "—"} ·{" "}
          {formatRelative(idea.createdAt)}
        </span>
      </div>
    </div>
  );
}
